import React, { useCallback, useState, useEffect } from 'react';
import { List } from 'react-window';
import { InfiniteLoader } from 'react-window-infinite-loader';
import { SkeletonList } from './Skeleton';
import { useTranslation } from 'react-i18next';

// Bypass React 18 / @types incompatibilities
interface VirtualListGenericProps {
    height: number | string;
    width: number | string;
    itemCount: number;
    itemSize: number;
    children: React.ComponentType<{ index: number; style: React.CSSProperties }>;
    overscanCount?: number;
    className?: string;
    onItemsRendered?: (params: { overscanStartIndex: number; overscanStopIndex: number; startIndex: number; stopIndex: number }) => void;
}
const VirtualList = List as unknown as React.ComponentType<VirtualListGenericProps>;

interface LoaderGenericProps {
    isRowLoaded: (index: number) => boolean;
    rowCount: number;
    loadMoreRows: (startIndex: number, stopIndex: number) => Promise<void> | void;
    threshold?: number;
    children: (props: { onRowsRendered: (params: { overscanStartIndex: number; overscanStopIndex: number; startIndex: number; stopIndex: number }) => void; registerChild: (ref: unknown) => void }) => React.ReactNode;
}
const Loader = InfiniteLoader as unknown as React.ComponentType<LoaderGenericProps>;

export interface VirtualizedListProps<T> {
    items: T[];
    itemHeight: number;
    containerHeight: number;
    renderItem: (item: T, index: number) => React.ReactNode;
    hasNextPage?: boolean;
    isNextPageLoading?: boolean;
    loadNextPage?: (startIndex: number, stopIndex: number) => Promise<void> | void;
    className?: string;
    overscanCount?: number;
}

/**
 * Composant de liste virtualisée pour les performances avec grandes listes
 * Utilise react-window pour éviter de rendre tous les éléments à la fois
 */
export function VirtualizedList<T>({
    items,
    itemHeight,
    containerHeight,
    renderItem,
    hasNextPage = false,
    isNextPageLoading: _isNextPageLoading = false,
    loadNextPage,
    className = '',
    overscanCount = 5,
}: VirtualizedListProps<T>) {
    const { t } = useTranslation('common');
    const [isLoading, setIsLoading] = useState(false);

    // Nombre total d'éléments (incluant les éléments à charger)
    const itemCount = hasNextPage ? items.length + 1 : items.length;

    // Fonction appelée par InfiniteLoader pour charger plus d'éléments
    const loadMoreItems = useCallback(
        async (startIndex: number, stopIndex: number) => {
            if (!loadNextPage || !hasNextPage) return;

            setIsLoading(true);
            try {
                await loadNextPage(startIndex, stopIndex);
            } finally {
                setIsLoading(false);
            }
        },
        [loadNextPage, hasNextPage]
    );

    // Vérifier si un élément est chargé
    const isItemLoaded = useCallback(
        (index: number) => !hasNextPage || index < items.length,
        [hasNextPage, items.length]
    );

    // Rendu d'un élément de la liste
    const ItemRenderer: React.FC<{ index: number; style: React.CSSProperties }> = ({ index, style }) => {
        let content: React.ReactNode;

        if (!isItemLoaded(index)) {
            // Élément en cours de chargement
            content = (
                <div className="p-4">
                    <SkeletonList count={1} />
                </div>
            );
        } else {
            // Élément chargé
            const item = items[index];
            content = renderItem(item, index);
        }

        return (
            <div style={style} className="virtualized-item">
                {content}
            </div>
        );
    };

    // Si pas de pagination infinie, utiliser simplement FixedSizeList
    if (!loadNextPage) {
        return (
            <div className={`virtualized-list ${className}`}>
                <VirtualList
                    height={containerHeight}
                    width="100%"
                    itemCount={items.length}
                    itemSize={itemHeight}
                    overscanCount={overscanCount}
                    className="w-full"
                >
                    {({ index, style }: { index: number; style: React.CSSProperties }) => (
                        <div style={style}>
                            {renderItem(items[index], index)}
                        </div>
                    )}
                </VirtualList>
            </div>
        );
    }

    // Avec pagination infinie
    return (
        <div className={`virtualized-list ${className}`}>
            <Loader
                isRowLoaded={isItemLoaded}
                rowCount={itemCount}
                loadMoreRows={loadMoreItems}
                threshold={5}
            >
                {({ onRowsRendered }: { onRowsRendered: (params: { overscanStartIndex: number; overscanStopIndex: number; startIndex: number; stopIndex: number }) => void }) => (
                    <VirtualList
                        height={containerHeight}
                        width="100%"
                        itemCount={itemCount}
                        itemSize={itemHeight}
                        onItemsRendered={onRowsRendered}
                        overscanCount={overscanCount}
                        className="w-full"
                    >
                        {ItemRenderer}
                    </VirtualList>
                )}
            </Loader>

            {/* Indicateur de chargement en bas */}
            {isLoading && hasNextPage && (
                <div className="flex justify-center p-8">
                    <div className="animate-spin rounded-full h-6 w-6 border-2 border-transparent border-t-blue-600 border-r-blue-600/30"></div>
                    <span className="ml-3 text-sm font-medium text-slate-500">{t('virtualizedList.loadingData')}</span>
                </div>
            )}
        </div>
    );
}

// eslint-disable-next-line react-refresh/only-export-components
export function useVirtualizedPagination<T>(
    queryFn: (page: number, limit: number) => Promise<{ data: T[]; total: number }>,
    options: {
        pageSize?: number;
        enabled?: boolean;
        cacheKey?: string;
    } = {}
) {
    const { pageSize = 20, enabled = true, cacheKey } = options;
    const [allItems, setAllItems] = useState<T[]>([]);
    const [hasNextPage, setHasNextPage] = useState(true);
    const [isLoading, setIsLoading] = useState(false);
    const [totalCount, setTotalCount] = useState(0);

    const loadNextPage = useCallback(
        async (startIndex: number, _stopIndex: number) => {
            if (!hasNextPage || isLoading) return;

            const page = Math.floor(startIndex / pageSize) + 1;
            setIsLoading(true);

            try {
                const result = await queryFn(page, pageSize);
                setAllItems(prev => [...prev, ...result.data]);
                setTotalCount(result.total);
                setHasNextPage(allItems.length + result.data.length < result.total);
            } catch (error) {
                console.error('Erreur lors du chargement de la page:', error);
            } finally {
                setIsLoading(false);
            }
        },
        [queryFn, pageSize, hasNextPage, isLoading, allItems.length]
    );

    // Réinitialiser lors du changement de cacheKey
    useEffect(() => {
        setAllItems([]);
        setHasNextPage(true);
        setTotalCount(0);
    }, [cacheKey]);

    return {
        items: allItems,
        hasNextPage,
        isNextPageLoading: isLoading,
        loadNextPage: enabled ? loadNextPage : undefined,
        totalCount,
    };
}
